import React, { useState } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    TableFooter,
    Paper,
    Chip,
    Typography,
    Box,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    TextField,
    InputAdornment,
    CircularProgress,
    Alert
} from "@mui/material";
import { Search, Close } from "@mui/icons-material";
import { useQuery } from "react-query";
import API from "../../../../../API";
import { StyledTableRow } from "../../../../../Utils/styledcell";
import useIsDesktop from "../../../../../Hooks/useIsDesktop";

const styles = {
    singleLine: { wordBreak: 'keep-all', whiteSpace: 'nowrap' },
};

export default function CreatorEmailsDialog({ open, onClose, username, onEmailClick }) {
    const [page, setPage] = useState(0);
    const [rowsPerPage] = useState(15);
    const [categoryFilter, setCategoryFilter] = useState('');
    const [sortOrder, setSortOrder] = useState('desc');
    const [searchQuery, setSearchQuery] = useState('');
    const [appliedSearch, setAppliedSearch] = useState('');

    const isDesktop = useIsDesktop();

    // Fetch creator emails
    const { data, isLoading, error, refetch } = useQuery({
        queryKey: ['creatorEmails', username, page, categoryFilter, sortOrder, appliedSearch],
        queryFn: () => API.blitzAI.getCreatorEmails(username, {
            page,
            rows: rowsPerPage,
            category: categoryFilter || undefined,
            sort_order: sortOrder,
            search: appliedSearch || undefined
        }),
        enabled: open && !!username,
        keepPreviousData: true,
    });

    const handleApplySearch = () => {
        setAppliedSearch(searchQuery);
        setPage(0);
    };

    const handleClearSearch = () => {
        setSearchQuery('');
        setAppliedSearch('');
        setPage(0);
    };

    const handleCategoryChange = (event) => {
        setCategoryFilter(event.target.value);
        setPage(0);
    };

    const handleSortChange = (event) => {
        setSortOrder(event.target.value);
        setPage(0);
    };

    const handleClose = () => {
        // Reset filters on close
        setPage(0);
        setCategoryFilter('');
        setSortOrder('desc');
        setSearchQuery('');
        setAppliedSearch('');
        onClose();
    };

    const getCategoryColor = (category) => {
        const colorMap = {
            'Paid': 'primary',
            'Affiliate': 'secondary',
            'Agency': 'info',
            'Negotiating': 'warning',
            'Signup': 'success',
            'Unpaid': 'default'
        };
        return colorMap[category] || 'default';
    };

    return (
        <Dialog 
            open={open} 
            onClose={handleClose} 
            maxWidth="lg" 
            fullWidth
            PaperProps={{
                sx: { height: '90vh' }
            }}
        >
            <DialogTitle>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                        <Typography variant="h6">
                            Email History: {data?.creator?.display_name || username}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            @{username} • {data?.total || 0} total emails
                        </Typography>
                    </Box>
                    <Button
                        size="small"
                        startIcon={<Close />}
                        onClick={handleClose}
                    >
                        Close
                    </Button>
                </Box>
            </DialogTitle>

            <DialogContent dividers>
                {/* Filters */}
                <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                        {/* Search */}
                        <TextField
                            size="small"
                            label="Search"
                            placeholder="Sender, subject, or content..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            onKeyPress={(e) => {
                                if (e.key === 'Enter') handleApplySearch();
                            }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Search />
                                    </InputAdornment>
                                ),
                            }}
                            sx={{ flexGrow: 1, minWidth: 250 }}
                        />

                        {/* Category Filter */}
                        <FormControl size="small" sx={{ minWidth: 150 }}>
                            <InputLabel>Category</InputLabel>
                            <Select
                                value={categoryFilter}
                                onChange={handleCategoryChange}
                                label="Category"
                            >
                                <MenuItem value="">
                                    <em>All Categories</em>
                                </MenuItem>
                                {data?.categories && Object.keys(data.categories).map((cat) => (
                                    <MenuItem key={cat} value={cat}>
                                        {cat} ({data.categories[cat]})
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        {/* Sort Order */}
                        <FormControl size="small" sx={{ minWidth: 150 }}>
                            <InputLabel>Sort by Date</InputLabel>
                            <Select
                                value={sortOrder}
                                onChange={handleSortChange}
                                label="Sort by Date"
                            >
                                <MenuItem value="desc">Newest First</MenuItem>
                                <MenuItem value="asc">Oldest First</MenuItem>
                            </Select>
                        </FormControl>

                        {/* Action Buttons */}
                        <Button
                            variant="contained"
                            onClick={handleApplySearch}
                            size="small"
                            sx={{ alignSelf: 'center' }}
                        >
                            Search
                        </Button>
                        {appliedSearch && (
                            <Button
                                variant="outlined"
                                onClick={handleClearSearch}
                                size="small"
                                sx={{ alignSelf: 'center' }}
                            >
                                Clear
                            </Button>
                        )}
                    </Box>

                    {/* Active Filters Display */}
                    {(appliedSearch || categoryFilter) && (
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                            <Typography variant="caption" color="text.secondary">
                                Active Filters:
                            </Typography>
                            {appliedSearch && (
                                <Chip
                                    size="small"
                                    label={`Search: ${appliedSearch}`}
                                    onDelete={handleClearSearch}
                                />
                            )}
                            {categoryFilter && (
                                <Chip
                                    size="small"
                                    label={`Category: ${categoryFilter}`}
                                    onDelete={() => {
                                        setCategoryFilter('');
                                        setPage(0);
                                    }}
                                />
                            )}
                        </Box>
                    )}
                </Box>

                {/* Loading State */}
                {isLoading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress />
                    </Box>
                )}

                {/* Error State */}
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        Failed to load emails: {error.message}
                    </Alert>
                )}

                {/* Email Table */}
                {!isLoading && !error && data && (
                    <TableContainer component={Paper} elevation={0}>
                        <Table size="small">
                            <TableHead>
                                <StyledTableRow>
                                    <TableCell sx={{ width: '15rem' }}>Sender</TableCell>
                                    <TableCell sx={{ width: '15rem' }}>Recipient</TableCell>
                                    <TableCell sx={{ minWidth: '25rem' }}>Content</TableCell>
                                    {isDesktop && <TableCell align='right'>Date</TableCell>}
                                    <TableCell align='center'>Category</TableCell>
                                    {isDesktop && <TableCell align='center'>Follow-up</TableCell>}
                                </StyledTableRow>
                            </TableHead>
                            <TableBody>
                                {data.emails.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={isDesktop ? 6 : 4} align="center">
                                            <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                                                No emails found matching your filters
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    data.emails.map((email) => (
                                        <StyledTableRow 
                                            key={email.id} 
                                            onClick={() => onEmailClick && onEmailClick(email.id)}
                                            sx={{ cursor: onEmailClick ? 'pointer' : 'default' }}
                                        >
                                            <TableCell sx={{ overflowX: 'hidden', width: '15rem', maxWidth: 0 }}>
                                                <span style={styles.singleLine}>{email.sender}</span>
                                            </TableCell>
                                            <TableCell sx={{ overflowX: 'hidden', width: '15rem', maxWidth: 0 }}>
                                                <span style={styles.singleLine}>{email.receiver}</span>
                                            </TableCell>
                                            <TableCell sx={{ overflowX: 'hidden', minWidth: '25rem', maxWidth: 0 }}>
                                                <span style={styles.singleLine}>
                                                    <strong>{email.subject}</strong>
                                                    {email.snippet && (
                                                        <Typography 
                                                            component="span" 
                                                            variant='caption' 
                                                            color='text.secondary'
                                                            sx={{ ml: 1 }}
                                                        >
                                                            {email.snippet}
                                                        </Typography>
                                                    )}
                                                </span>
                                            </TableCell>
                                            {isDesktop && (
                                                <TableCell align='right'>
                                                    <span style={styles.singleLine}>
                                                        {new Date(email.date_received).toLocaleString()}
                                                    </span>
                                                </TableCell>
                                            )}
                                            <TableCell align='center'>
                                                {email.category ? (
                                                    <Chip 
                                                        label={email.category} 
                                                        color={getCategoryColor(email.category)}
                                                        size="small"
                                                    />
                                                ) : (
                                                    <Typography variant="caption" color="text.secondary">
                                                        N/A
                                                    </Typography>
                                                )}
                                            </TableCell>
                                            {isDesktop && (
                                                <TableCell align='center'>
                                                    {email.follow_up_stage > 0 ? (
                                                        <Chip 
                                                            label={`Stage ${email.follow_up_stage}`}
                                                            size="small"
                                                            variant="outlined"
                                                        />
                                                    ) : (
                                                        <Typography variant="caption" color="text.secondary">
                                                            -
                                                        </Typography>
                                                    )}
                                                </TableCell>
                                            )}
                                        </StyledTableRow>
                                    ))
                                )}
                            </TableBody>
                            <TableFooter>
                                <TableRow>
                                    <TablePagination
                                        colSpan={isDesktop ? 6 : 4}
                                        count={data.total}
                                        rowsPerPage={rowsPerPage}
                                        page={page}
                                        onPageChange={(event, newPage) => setPage(newPage)}
                                        rowsPerPageOptions={[rowsPerPage]}
                                    />
                                </TableRow>
                            </TableFooter>
                        </Table>
                    </TableContainer>
                )}
            </DialogContent>

            <DialogActions>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', px: 2 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>
                        {data && `Showing ${data.emails.length} of ${data.total} emails`}
                    </Typography>
                    <Button onClick={handleClose} variant="contained">
                        Close
                    </Button>
                </Box>
            </DialogActions>
        </Dialog>
    );
}